// Plans (simple-mode S4): where every upsell link points.
//
// The Owner has not ruled on the upsell CTA (next-cycle-overview open question 9: "with
// no billing, where does Upgrade lead?"), and the simple-mode scope says no upsell copy
// or prices until then. So this page only states facts from GET /entitlements: the
// active workspace's plan, what it includes, current usage, one-time credits, and how a
// plan is changed today (by Symone, on request). No prices, no checkout.
import { useEntitlements, creditsState, featureOn, planName } from '../entitlements'
import { PlanSummary } from '../entitlements/PlanSummary'
import { PageHeader, QueryState } from '../components/ui'

const SUPPORT_URL: string | undefined = import.meta.env.VITE_SUPPORT_URL

// Presentation labels for the features a non-developer recognises. The values, limits
// and remedies all come from the server; this only names the rows.
const INCLUDED: { key: string; label: string }[] = [
  { key: 'connect_claude', label: 'Connect to Claude' },
  { key: 'developer_mode', label: 'Developer mode' },
  { key: 'webhooks.config', label: 'Webhooks' },
  { key: 'skills.workspace', label: 'Workspace skills' },
  { key: 'policy.allowlist_mode', label: 'Allowlist mode' },
  { key: 'auth_configs.byo', label: 'Bring your own OAuth app' },
  { key: 'platform.end_users', label: 'End-users, connect links and tool sessions' },
]

const LIMITS: { key: string; label: string }[] = [
  { key: 'workspaces.max', label: 'Workspaces you own' },
  { key: 'members.max', label: 'Members per workspace' },
  { key: 'apps.max', label: 'Connected apps' },
  { key: 'triggers.max', label: 'Triggers' },
  { key: 'calls.rate_per_minute', label: 'Tool calls per minute' },
]

function limitValue(raw: unknown): string {
  if (raw === null) return 'Unlimited'
  if (typeof raw === 'number') return String(raw)
  return 'Not included'
}

export function PlansView() {
  const q = useEntitlements()
  const ent = q.data
  const credits = creditsState(ent)
  return (
    <div className="page page--narrow">
      <PageHeader title="Plans" description="What this workspace’s plan includes, what it has used, and how to change it." />
      <QueryState isLoading={q.isLoading} error={q.error} what="your plan" onRetry={() => q.refetch()}>
        {ent && (
          <>
            <section className="card-section" aria-labelledby="plan-current-title">
              <h2 id="plan-current-title" className="section-title">Current plan: {planName(ent)}</h2>
              {credits && (
                <p className={credits.exhausted ? 'warn-note' : 'muted'} role={credits.exhausted ? 'alert' : undefined}>
                  {credits.exhausted
                    ? `This workspace has used all of its free credits, so tool calls are paused. ${credits.remedy}`
                    : `This plan comes with one-time free credits: one credit per tool call. ${credits.balance} left.`}
                </p>
              )}
              <PlanSummary ent={ent} />
            </section>

            <section className="card-section" aria-labelledby="plan-includes-title">
              <h2 id="plan-includes-title" className="section-title">Included</h2>
              <table className="table table--dense" aria-label="Plan features">
                <tbody>
                  {INCLUDED.map(({ key, label }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>{featureOn(ent, key) ? 'Included' : 'Not included'}</td>
                    </tr>
                  ))}
                  {LIMITS.map(({ key, label }) => (
                    <tr key={key}>
                      <td>{label}</td>
                      <td>{limitValue(ent.features[key]?.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section className="card-section" aria-labelledby="plan-change-title">
              <h2 id="plan-change-title" className="section-title">Changing your plan</h2>
              <p>
                Plans aren’t self-serve yet: Symone changes a workspace’s plan on request. Ask your workspace owner, or{' '}
                {SUPPORT_URL ? (
                  <a href={SUPPORT_URL} target="_blank" rel="noreferrer">contact Symone support</a>
                ) : (
                  'contact Symone support'
                )}
                . Your data and connected apps stay as they are when the plan changes
                {credits ? ', and any free credits left are kept' : ''}.
              </p>
            </section>
          </>
        )}
      </QueryState>
    </div>
  )
}
